import { Button, FloatingIndicator, Group, Paper, type PaperProps, Tooltip } from "@mantine/core"
import { createContext, type PropsWithChildren, use, useState } from "react"
import { useMinWidth } from "@/hooks"
import classes from "./Pills.module.css"

const ctx = createContext<any>({ value: null, setValue: () => null })
const ctxRef = createContext<any>(() => null)

export function Pills({
  children,
  value,
  onChange,
}: PropsWithChildren<{ value?: string; onChange?: (value: string) => void }>) {
  const [v, setValue] = useState(value)
  return <ctx.Provider value={{ value: value || v, setValue: onChange ?? setValue }}>{children}</ctx.Provider>
}

function PillsTabs({ children, ...props }: PropsWithChildren<PaperProps>) {
  const { value } = use(ctx)
  const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null)
  const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({})
  const setControlRef = (val: string) => (node: HTMLButtonElement) => {
    controlsRefs[val] = node
    setControlsRefs(controlsRefs)
  }

  return (
    <Paper radius="xl" p={5} w="max-content" {...props}>
      <Group justify="space-evenly" gap={4} ref={setRootRef} pos="relative">
        <ctxRef.Provider value={setControlRef}>{children}</ctxRef.Provider>
        <FloatingIndicator parent={rootRef} target={value ? controlsRefs[value] : null} className={classes.indicator} />
      </Group>
    </Paper>
  )
}

Pills.Tabs = PillsTabs

function PillsPanel({ children, value }: PropsWithChildren<{ value: string }>) {
  const { value: curValue } = use(ctx)
  return value == curValue ? children : null
}

Pills.Panel = PillsPanel

function PillsTab({
  value,
  title,
  children,
  ...props
}: PropsWithChildren<{ value: string; title?: string } & Parameters<typeof Button<any>>[0]>) {
  const { value: curValue, setValue } = use(ctx)
  const setControlRef = use(ctxRef)
  const hasDesktopWidth = useMinWidth("lg")
  const isActive = value == curValue
  const isCompact = !hasDesktopWidth && !!props.leftSection
  const label = title ?? children
  const button = (
    <Button
      px="md"
      ref={setControlRef(value)}
      pos="relative"
      fw={500}
      size="sm"
      c={isActive ? "white" : "var(--mantine-color-text)"}
      onClick={() => setValue(value)}
      variant={isActive ? "filled" : "subtle"}
      {...props}
      leftSection={isCompact ? undefined : props.leftSection}
      className={classes.tab}
    >
      {isCompact ? props.leftSection : label}
    </Button>
  )

  if (!isCompact || !title) return button

  return <Tooltip label={title}>{button}</Tooltip>
}

Pills.Tab = PillsTab
